import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import TextContent from '@risk-smart/themed-cloudscape-components/text-content';
import type { FC } from 'react';
import { useEffect, useRef } from 'react';

import { AISidePanelLoading } from '@/components/side-panel/ai/AISidePanelLoading';
import { useIsFormPrefillEnabled } from '@/hooks/useIsFormPrefillEnabled';

import { AgentActivity } from './agent-activity/AgentActivity';
import {
  isAgentActivityDemoEnabled,
  seedAgentActivityDemo,
} from './agent-activity/agentActivityDemo';
import { ChatBetaWarningBanner } from './ChatBetaWarningBanner';
import { MarkdownMessage } from './MarkdownMessage';
import { MessageFeedback } from './MessageFeedback';
import { ProposalCard } from './proposal/ProposalCard';
import styles from './style.module.scss';
import { type ChatMessage, useChatStore } from './useChatStore';

interface ChatMessagesProps {
  messages: ChatMessage[];
  isLoading: boolean;
  isInitialising: boolean;
}

export const ChatMessages: FC<ChatMessagesProps> = ({
  messages,
  isLoading,
  isInitialising,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const formPrefillEnabled = useIsFormPrefillEnabled();
  const {
    currentThinkingStep,
    streamingMessageId,
    isStreaming,
    sessionId,
    chatMode,
  } = useChatStore();

  // Auto-scroll to bottom when new messages are added or loading state changes
  useEffect(() => {
    const scrollToBottom = () => {
      if (messagesEndRef.current && messagesContainerRef.current) {
        // Method 1: Scroll the container to the very bottom
        const container = messagesContainerRef.current;
        container.scrollTop = container.scrollHeight;

        // Method 2: Also use scrollIntoView as backup for smooth effect
        setTimeout(() => {
          if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({
              behavior: 'smooth',
              block: 'end',
              inline: 'nearest',
            });
          }
        }, 50);
      }
    };

    // Small delay to ensure DOM has updated
    const timeoutId = setTimeout(scrollToBottom, 100);

    return () => clearTimeout(timeoutId);
  }, [messages, isLoading, isInitialising]);

  // Scroll during streaming - using a more conservative approach
  useEffect(() => {
    if (!isStreaming) {
      return;
    }

    // Use requestAnimationFrame for smoother performance instead of setInterval
    let animationFrameId: number;
    let lastScrollHeight = 0;

    const checkAndScroll = () => {
      if (messagesContainerRef.current) {
        const container = messagesContainerRef.current;
        const currentScrollHeight = container.scrollHeight;

        // Only scroll if content height has actually changed
        if (currentScrollHeight !== lastScrollHeight) {
          container.scrollTop = currentScrollHeight;
          lastScrollHeight = currentScrollHeight;
        }
      }

      // Continue checking while streaming
      if (isStreaming) {
        animationFrameId = requestAnimationFrame(checkAndScroll);
      }
    };

    // Start the animation frame loop
    animationFrameId = requestAnimationFrame(checkAndScroll);

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [isStreaming]);

  return (
    <div
      ref={messagesContainerRef}
      className={styles.messagesContainer}
      data-testid={'chat-messages'}
    >
      <ChatBetaWarningBanner />
      {/* Agent Activity preview harness — gated OFF by default (see agentActivityDemo). */}
      {isAgentActivityDemoEnabled() && (
        <button
          type={'button'}
          onClick={seedAgentActivityDemo}
          style={{
            display: 'block',
            width: 'calc(100% - 16px)',
            margin: '8px',
            padding: '8px 10px',
            fontSize: 13,
            fontWeight: 600,
            border: '2px dashed #1AAA9E',
            borderRadius: 8,
            background: '#EAFBF9',
            color: '#0F7A70',
            cursor: 'pointer',
          }}
        >
          {'▶ Preview ALL Agent Activity states'}
        </button>
      )}
      {isInitialising && <AISidePanelLoading></AISidePanelLoading>}
      {!isInitialising && messages.length === 0 ? (
        <div className={'px-4 py-2 m-5'} data-testid={'chat-empty-state'}>
          <TextContent>
            {chatMode === 'agent' ? (
              <>
                <h3 className={'ms-0 mt-0 mb-8'}>
                  {"Hello! 👋 I'm the RiskSmart Agent"}
                </h3>
                <p>
                  {
                    'I can look across your GRC data — risks 📊, controls 🛡️, actions ✅, policies 📋 and more — to answer questions and summarise what I find.'
                  }
                </p>
                <p>
                  {
                    'Ask me anything, or pick one of the suggestions below to get started. 🚀'
                  }
                </p>
              </>
            ) : (
              <>
                <h3 className={'ms-0 mt-0 mb-8'}>{'Hello!'}</h3>
                <p>
                  {
                    'Unfortunately, something has gone wrong with the Platform Knowledge AI.'
                  }
                </p>
                <p>
                  {
                    'Please try refreshing the page or contact support if the issue persists.'
                  }
                </p>
              </>
            )}
          </TextContent>
        </div>
      ) : (
        !isInitialising && (
          <SpaceBetween size={'s'}>
            {messages.map((message, index) => {
              const isStreamingMessage =
                isStreaming && message.id === streamingMessageId;

              return (
                <div
                  key={message.id}
                  className={`${styles.message} ${
                    message.isSystem
                      ? styles.systemMessage
                      : message.isUser
                        ? styles.userMessage
                        : styles.botMessage
                  }`}
                  data-testid={`chat-message-${
                    message.isSystem ? 'system' : message.isUser ? 'user' : 'ai'
                  }`}
                >
                  <div className={styles.messageContent}>
                    <MarkdownMessage
                      content={message.content}
                      isUser={message.isUser}
                    />
                    {isStreamingMessage && (
                      <span
                        className={styles.streamingDots}
                        data-testid={'streaming-dots'}
                      >
                        <div className={styles.streamingDot}></div>
                        <div className={styles.streamingDot}></div>
                        <div className={styles.streamingDot}></div>
                      </span>
                    )}
                  </div>
                  {!message.isUser &&
                    !message.isSystem &&
                    // Agent-mode only — the "Agent activity" timeline is fed by
                    // the gateway-populated `activity` log, which the platform
                    // Knowledge chat never sets, so this leaves that UI
                    // untouched. Rendered beneath the bubble, not inside it.
                    // Also render while an agent message is still streaming (even
                    // before the first activity frame) so the live "Working…"
                    // state is reachable; it self-hides once the turn ends with
                    // no activity.
                    ((message.metadata?.activity?.length ?? 0) > 0 ||
                      (chatMode === 'agent' && isStreamingMessage)) && (
                      <div className={styles.toolCalls}>
                        <AgentActivity
                          events={message.metadata?.activity ?? []}
                          isStreaming={isStreamingMessage}
                        />
                      </div>
                    )}
                  {/* Suggest-and-confirm form prefill (AID-381). Only agent
                      turns ever carry a proposal — the platform Knowledge chat
                      has no path that sets it — so this leaves that mode
                      untouched, exactly like the activity timeline above.
                      Flag-gated (AID-382) so a proposal that somehow arrives
                      with the feature off is never actionable. */}
                  {formPrefillEnabled && message.metadata?.proposal && (
                    <ProposalCard
                      messageId={message.id}
                      proposal={message.metadata.proposal}
                    />
                  )}
                  <div className={styles.messageFooter}>
                    <div className={styles.messageTime}>
                      <TextContent>
                        <small>
                          {message.timestamp.toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </small>
                      </TextContent>
                    </div>
                    {!message.isUser &&
                      !message.isSystem &&
                      !isStreamingMessage &&
                      sessionId && (
                        <MessageFeedback
                          message={message}
                          sessionId={sessionId}
                          previousUserMessage={
                            messages.slice(0, index).findLast((m) => m.isUser)
                              ?.content
                          }
                        />
                      )}
                  </div>
                </div>
              );
            })}
          </SpaceBetween>
        )
      )}
      {isLoading && !isStreaming && (
        <div className={styles.loadingMessage} data-testid={'chat-loading'}>
          <div className={styles.thinkingContainer}>
            <div className={styles.thinkingBubble}>
              <div className={styles.thinkingDot}></div>
              <div className={styles.thinkingDot}></div>
              <div className={styles.thinkingDot}></div>
            </div>
            {currentThinkingStep && (
              <div
                className={styles.thinkingStep}
                data-testid={'thinking-step'}
              >
                {currentThinkingStep}
              </div>
            )}
          </div>
        </div>
      )}
      {/* Invisible element to scroll to */}
      <div
        ref={messagesEndRef}
        style={{ height: '1px', marginBottom: '16px' }}
      />
    </div>
  );
};
